import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Appointments() {
  const [appts, setAppts] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { title: "", customer_name: "", customer_phone: "", customer_email: "", start_at: "", end_at: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/appointments").then((r) => setAppts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, start_at: new Date(form.start_at).toISOString(), end_at: new Date(form.end_at).toISOString() };
      await api.post("/appointments", payload);
      toast.success("Appointment booked"); setOpen(false); setForm(empty); load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Cancel this appointment?")) return;
    await api.delete(`/appointments/${id}`); toast.success("Cancelled"); load();
  };

  return (
    <div data-testid="appointments-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Appointments</h1>
          <p className="text-sm text-[#4B5563]">Bookings created by your agents and team.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2" data-testid="new-appt-button"><Plus className="w-4 h-4" /> New</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New appointment</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "title", label: "Title", type: "text", span: 2 },
                { k: "customer_name", label: "Customer name", type: "text" },
                { k: "customer_phone", label: "Phone", type: "text" },
                { k: "customer_email", label: "Email", type: "email", span: 2 },
                { k: "start_at", label: "Start", type: "datetime-local" },
                { k: "end_at", label: "End", type: "datetime-local" },
              ].map(({ k, label, type, span }) => (
                <div key={k} className={span === 2 ? "col-span-2" : ""}>
                  <label className="text-xs font-medium block mb-1">{label}</label>
                  <input data-testid={`appt-${k}-input`} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
                </div>
              ))}
            </div>
            <button data-testid="appt-save-button" onClick={save} disabled={busy || !form.title || !form.start_at} className="btn-primary w-full mt-4">{busy ? "Saving…" : "Book"}</button>
          </DialogContent>
        </Dialog>
      </div>

      {appts.length === 0 ? (
        <div className="hs-card p-12 text-center">
          <CalendarDays className="w-10 h-10 mx-auto text-[#4B5563]" />
          <div className="font-display font-bold mt-4">No appointments</div>
          <div className="text-sm text-[#4B5563]">Schedule one or let your agents do it.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {appts.map((a) => (
            <div key={a.id} className="hs-card p-4 flex items-center justify-between" data-testid={`appt-row-${a.id}`}>
              <div>
                <div className="font-display font-bold">{a.title}</div>
                <div className="text-xs text-[#4B5563] font-mono">{a.customer_name} · {a.customer_phone || a.customer_email}</div>
              </div>
              <div className="text-sm font-mono text-right">
                <div>{a.start_at?.replace("T", " ").slice(0, 16)}</div>
                <div className="text-xs text-[#4B5563]">→ {a.end_at?.replace("T", " ").slice(11, 16)}</div>
              </div>
              <button onClick={() => remove(a.id)} className="text-[#EF4444] p-1" data-testid={`appt-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
